import React, { useCallback, useMemo, useState } from 'react';
import {
  CellTypes,
  DataGrid,
  DataGridProps,
  GridColDef,
  Icon,
  Tooltip,
  Badge,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import { findLast, invert, sumBy } from 'lodash';
import numeral from 'numeral';
import {
  CartonLabelModal,
  HangtagModal,
  UnitLabelModal,
} from '../ReusableComponents/NewDesign/Modals/PrintLabelModal';
import {
  PurchaseOrderLine,
  PurchaseOrder,
  purchaseOrderLineFieldNames,
} from '../../Lib/Types/purchase-order';
import DimensionsStatus from '../FnCatalogV2/DimensionsStatus/DimensionsStatus';
import TopStatuses from '../../Lib/Constants/top-statuses';
import TopStatusVendorDisplay from '../../Lib/Constants/top-status-vendor-display';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';
import sumPoItemsCost from './Utils/sumPoItemsCost';
import {
  CleanedPurchaseOrderLine,
  cleanPurchaseOrderLine,
} from './Utils/cleanPurchaseOrderLine';
import {
  SizeCell,
  StringCell,
  NumberCell,
  UnitCostCell,
  PrintButtonCell,
  LinkCell,
  BooleanCell,
} from './PurchaseOrderDetailDataGridCells';
import { User } from '../../Lib/Api/vendor';
import {
  canPrintUnitLabel,
  canPrintHangtag,
  canPrintCarton,
} from '../../Lib/Utils/Asn/printingApproved';
import { useCurrentUser } from '../../Lib/Contexts/CurrentUserContext';
import toast from '../Toast/Toast';
import {
  isLineChargeback,
  isLineReceived,
  isLineSubtotal,
  isPoFullyReceived,
} from './Utils/poStatusCheck';

type Row = {
  id: string;
  item: CellTypes.DataGridCellCustomParams;
  fn_upc: CellTypes.DataGridCellCustomParams;
  vendor_style: CellTypes.DataGridCellCustomParams;
  prop65?: CellTypes.DataGridCellCustomParams;
  category: CellTypes.DataGridCellCustomParams;
  department: CellTypes.DataGridCellCustomParams;
  print_unit_label: CellTypes.DataGridCellCustomParams;
  print_hang_tags: CellTypes.DataGridCellCustomParams;
  print_carton_label: CellTypes.DataGridCellCustomParams;
  fn_style_number: CellTypes.DataGridCellCustomParams;
  color_label: CellTypes.DataGridCellCustomParams;
  size: CellTypes.DataGridCellCustomParams;
  item_size_weight: CellTypes.DataGridCellCustomParams;
  item_top_status: CellTypes.DataGridCellBadgeParams | null;
  unit_cost: CellTypes.DataGridCellCustomParams;
  quantity: CellTypes.DataGridCellCustomParams;
  quantity_received: CellTypes.DataGridCellCustomParams;
  ext_cost: CellTypes.DataGridCellCustomParams;
};

const columns: GridColDef[] = [
  {
    field: 'item',
    headerName: 'Item',
    render: 'custom',
    display: 'flex',
    flex: 3,
    sortable: false,
    minWidth: 300,
    disableColumnMenu: true,
  },
  {
    field: 'fn_upc',
    headerName: 'FN UPC',
    render: 'custom',
    display: 'flex',
    flex: 2,
    sortable: true,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'vendor_style',
    headerName: 'Vendor Style #',
    render: 'custom',
    display: 'flex',
    flex: 2,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'prop65',
    renderHeader: () => (
      <Stack
        direction="row"
        align="center"
        gap="sm"
        className="text-[#1D24338C] cursor-pointer text-xs"
        onClick={() => {
          window.open(
            'https://www.p65warnings.ca.gov/sites/default/files/art_6_business_qa.pdf',
            '_blank',
          );
        }}
      >
        <span>CA Prop 65</span>
        <Icon variant="solid" name="information/information-circle" />
      </Stack>
    ),
    render: 'custom',
    display: 'flex',
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
    sortable: false,
  },
  {
    field: 'category',
    headerName: 'Category',
    render: 'custom',
    display: 'flex',
    flex: 2,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'department',
    headerName: 'Department',
    render: 'custom',
    display: 'flex',
    flex: 2,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'print_unit_label',
    headerName: 'Print Unit Labels',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 120,
    disableColumnMenu: true,
  },
  {
    field: 'print_hang_tags',
    headerName: 'Print Hangtags',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 120,
    disableColumnMenu: true,
  },
  {
    field: 'print_carton_label',
    headerName: 'Print Carton Labels',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 120,
    disableColumnMenu: true,
  },
  {
    field: 'fn_style_number',
    headerName: 'FN Style Number',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'color_label',
    headerName: 'Color Label',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    minWidth: 180,
    disableColumnMenu: true,
  },
  {
    field: 'size',
    headerName: 'Item Size',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    minWidth: 80,
    disableColumnMenu: true,
  },
  {
    field: 'item_size_weight',
    headerName: 'Size & Weight',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: false,
    minWidth: 250,
    disableColumnMenu: true,
  },
  {
    field: 'item_top_status',
    headerName: 'Item TOP Status',
    render: 'badge',
    display: 'flex',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'unit_cost',
    headerName: 'Unit Cost',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    type: 'number',
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    type: 'number',
    minWidth: 80,
    disableColumnMenu: true,
  },
  {
    field: 'quantity_received',
    headerName: 'Qty. Received',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    type: 'number',
    minWidth: 80,
    disableColumnMenu: true,
  },
  {
    field: 'ext_cost',
    headerName: 'Ext. Cost',
    render: 'custom',
    display: 'flex',
    flex: 1,
    sortable: true,
    type: 'number',
    minWidth: 150,
    disableColumnMenu: true,
    description: 'Ext. Cost = Cost after it was received.',
  },
];

type Props = {
  purchaseOrder: PurchaseOrder;
  refetch: () => void;
  userId: string;
  isPoVendorAccepted: boolean;
  onDimensionOpen: (item: unknown) => void;
  perms: {
    canPrint: boolean;
    canPrintWithoutVendorAcceptance: boolean;
    canUpdateSizeAndWeight: boolean;
    canViewCost: boolean;
  };
  merchType: string;
  user: User;
  onProp65DrawerOpen: () => void;
} & Omit<DataGridProps<Row>, 'columns'>;
function PurchaseOrderDetailDataGrid({
  purchaseOrder,
  refetch,
  userId,
  isPoVendorAccepted,
  onDimensionOpen,
  perms,
  merchType,
  user,
  onProp65DrawerOpen,
  ...dataGridProps
}: Props) {
  const [state, setState] = useState<{
    printPOInformation?: PurchaseOrderLine;
    openPrintUnitLabel: boolean;
    openPrintHangtag: boolean;
    openPrintCartonLabel: boolean;
  }>({
    printPOInformation: undefined,
    openPrintUnitLabel: false,
    openPrintHangtag: false,
    openPrintCartonLabel: false,
  });
  const { flags } = React.useContext(FeatureFlagsContext);
  const statusColorMap: { [key: string]: 'green' | 'yellow' | 'red' } = {
    Approved: 'green',
    Rejected: 'red',
    'Pending Receipt': 'yellow',
  };
  const { purchase_order_lines: rows, po_number: poNumber } = purchaseOrder;

  const isFullyReceived = React.useMemo(
    () => isPoFullyReceived(purchaseOrder),
    [purchaseOrder],
  );
  const { currentUser } = useCurrentUser();
  const canPrintUnit = canPrintUnitLabel(user, currentUser) && perms.canPrint;
  const canPrintHang = canPrintHangtag(user, currentUser) && perms.canPrint;
  const canPrintCart = canPrintCarton(user, currentUser) && perms.canPrint;

  const isProp65Completed = React.useMemo(
    () =>
      purchaseOrder.purchase_order_lines.every(
        (line) => line.prop65 !== null && line.prop65 !== undefined,
      ),
    [purchaseOrder],
  );

  const kidsDivision = React.useMemo(
    () => purchaseOrder.division === "KID'S",
    [purchaseOrder],
  );

  const toastProp65 = React.useCallback(
    (onClick: () => void) => {
      if (isProp65Completed || kidsDivision || !flags.prop65?.enabled) {
        onClick();
        return;
      }
      toast(
        {
          title: 'CA Prop 65 required to print.',
          text: 'Select ‘Yes’ if the item is Prop 65, and ‘No’ if it is not Prop 65.',
        },
        { type: 'error' },
      );
    },
    [isProp65Completed, kidsDivision],
  );

  const handlePrintUnitLabelModal = (lineItem: PurchaseOrderLine) => {
    setState((oldState) => ({
      ...oldState,
      printPOInformation: { ...lineItem, po_number: poNumber },
      openPrintUnitLabel: true,
    }));
  };

  const handlePrintHangtagModal = (lineItem: PurchaseOrderLine) => {
    setState((oldState) => ({
      ...oldState,
      printPOInformation: { ...lineItem, po_number: poNumber },
      openPrintHangtag: true,
    }));
  };

  const handlePrintCartonLabelModal = (lineItem: PurchaseOrderLine) => {
    setState((oldState) => ({
      ...oldState,
      printPOInformation: { ...lineItem, po_number: poNumber },
      openPrintCartonLabel: true,
    }));
  };

  const closeModal = () => {
    setState((oldState) => ({
      ...oldState,
      openPrintUnitLabel: false,
      openPrintHangtag: false,
      openPrintCartonLabel: false,
      printPOInformation: undefined,
    }));
  };

  const confirmPrintModal = () => {
    refetch();
    closeModal();
  };

  const getLineItemTopStatus = (lineItem: PurchaseOrderLine) => {
    const key = invert(TopStatuses)[
      lineItem.top_status as keyof typeof TopStatuses
    ] as keyof typeof TopStatusVendorDisplay;
    return TopStatusVendorDisplay[key];
  };

  const prop65Cell = useCallback(
    (cleanLine: CleanedPurchaseOrderLine) => {
      if (kidsDivision) {
        return (
          <div className="flex gap-2 items-center justify-center">
            <span>No</span>
            <Tooltip
              content="Fashion Nova does NOT accept any children's products that require Prop 65 warning."
              arrow
            >
              <Icon
                name="information/information-circle"
                color="#1D24338C"
                className="cursor-pointer"
                onClick={() => {
                  window.open(
                    'https://www.p65warnings.ca.gov/sites/default/files/art_6_business_qa.pdf',
                    '_blank',
                  );
                }}
              />
            </Tooltip>
          </div>
        );
      }
      if (isProp65Completed) {
        return <BooleanCell data={cleanLine.prop65.currentValue} />;
      }
      return (
        <LinkCell
          data={{
            title: 'Set',
            onClick: () => {
              onProp65DrawerOpen();
            },
          }}
        />
      );
    },
    [kidsDivision, isProp65Completed],
  );

  const transformedRows = useMemo(
    () =>
      rows.map((lineItem) => {
        const cleanLine = cleanPurchaseOrderLine(lineItem);
        const isSubtotal = isLineSubtotal(lineItem);
        const topStatus = getLineItemTopStatus(lineItem);
        const isChargeback = isLineChargeback(lineItem);
        const baseCanPrint =
          !isSubtotal &&
          !isChargeback &&
          (isPoVendorAccepted || perms.canPrintWithoutVendorAcceptance);
        const canPrintUnitLabelRow = baseCanPrint && canPrintUnit;
        const canPrintHangtagRow = baseCanPrint && canPrintHang;
        const canPrintCartonRow = baseCanPrint && canPrintCart;
        const displayVariant = isPoVendorAccepted ? 'secondary' : 'primary';
        return {
          id: lineItem.po_line_id.toString(),
          item: (
            <div className="flex w-[96%]">
              {!isPoVendorAccepted && cleanLine.is_line_updated ? (
                <Tooltip
                  content={`Revision made to: ${cleanLine.fields_changed
                    ?.map((field) => purchaseOrderLineFieldNames[field])
                    .join(', ')}`}
                  position="top"
                  arrow
                >
                  <Icon.Flag
                    className="min-w-4 text-warning-700 mr-2"
                    variant="solid"
                    size="16px"
                  />
                </Tooltip>
              ) : null}
              {!isPoVendorAccepted && cleanLine.is_new_line ? (
                <Badge color="yellow" className="mr-2">
                  New
                </Badge>
              ) : null}
              <StringCell data={cleanLine.item_name} variant={displayVariant} />
            </div>
          ),
          fn_upc: !isSubtotal ? (
            <StringCell data={cleanLine.upc} variant={displayVariant} />
          ) : (
            ''
          ),
          vendor_style: (
            <StringCell
              data={cleanLine.vendor_style}
              variant={displayVariant}
            />
          ),
          ...(flags.prop65 &&
            flags.prop65.enabled && { prop65: prop65Cell(cleanLine) }),
          category: (
            <StringCell data={cleanLine.category} variant={displayVariant} />
          ),
          department: (
            <StringCell data={cleanLine.department} variant={displayVariant} />
          ),
          print_unit_label: canPrintUnitLabelRow ? (
            <PrintButtonCell
              printFn={() =>
                toastProp65(() => handlePrintUnitLabelModal(lineItem))
              }
              isChecked={!!lineItem.dual_sticker_status}
              printIcon="bag/paper-bag"
              prop65={!isProp65Completed && !kidsDivision}
            />
          ) : null,
          print_hang_tags: canPrintHangtagRow ? (
            <PrintButtonCell
              printFn={() =>
                toastProp65(() => handlePrintHangtagModal(lineItem))
              }
              isChecked={!!lineItem.hangtag_sticker_status}
              printIcon="tag"
              prop65={!isProp65Completed && !kidsDivision}
            />
          ) : null,
          print_carton_label: canPrintCartonRow ? (
            <PrintButtonCell
              printFn={() =>
                toastProp65(() => handlePrintCartonLabelModal(lineItem))
              }
              isChecked={!!lineItem.carton_label_status}
              printIcon="package/package-02"
              prop65={!isProp65Completed && !kidsDivision}
            />
          ) : null,
          fn_style_number: (
            <StringCell data={cleanLine.sku} variant={displayVariant} />
          ),
          color_label: (
            <StringCell data={cleanLine.color_label} variant={displayVariant} />
          ),
          size: <SizeCell data={cleanLine.size} variant={displayVariant} />,
          item_size_weight: lineItem.catalog_item ? (
            <DimensionsStatus
              perms={perms}
              catalogItem={lineItem.catalog_item}
              onDimensionOpen={() => {
                onDimensionOpen(lineItem.catalog_item);
              }}
            />
          ) : null,
          item_top_status: topStatus
            ? {
                children: topStatus,
                contrast: 'low',
                color: statusColorMap[topStatus],
              }
            : null,
          unit_cost: (
            <UnitCostCell
              data={cleanLine.price}
              isChargeback={isChargeback}
              isSubtotal={isSubtotal}
              variant={displayVariant}
            />
          ),
          quantity: !isSubtotal ? (
            <NumberCell data={cleanLine.quantity} variant={displayVariant} />
          ) : (
            '-'
          ),
          quantity_received: !isSubtotal ? (
            <NumberCell
              data={{ currentValue: cleanLine.quantity_received }}
              variant={displayVariant}
            />
          ) : (
            ''
          ),
          ext_cost:
            (!purchaseOrder.delivery || purchaseOrder.delivery.delivered_at) &&
            ((cleanLine.quantity_received && cleanLine.quantity_received > 0) ||
              isChargeback ||
              (isSubtotal && isFullyReceived)) ? (
              <NumberCell
                data={{ currentValue: cleanLine.total }}
                format="$0,0.00"
                variant={displayVariant}
              />
            ) : (
              '-'
            ),
        } satisfies Row;
      }),
    [
      rows,
      isPoVendorAccepted,
      canPrintUnit,
      canPrintHang,
      canPrintCart,
      perms.canPrintWithoutVendorAcceptance,
    ],
  );

  const lastSubtotal = rows
    ? findLast(rows, (item) => item.item_name.includes('Subtotal'))
    : undefined;

  const quantityAggregation = {
    apply: () => numeral(sumBy(rows, 'quantity')).format('0,0'),
    label: '',
  };

  const totalAggregation = {
    apply: () => 'Total',
    label: '',
  };

  const quantityReceivedAggregation = {
    apply: () => {
      const hasReceivedLineOrChargeback = rows.some(
        (item) => isLineReceived(item) || isLineChargeback(item),
      );
      if (!hasReceivedLineOrChargeback) return '-';
      return numeral(
        sumBy(
          rows.filter((item) => item.item_name !== 'Subtotal'),
          'quantity_received',
        ),
      ).format('0,0');
    },
    label: '',
  };

  const extCostAggregation = {
    apply: () => {
      const hasReceivedLineOrChargeback = rows.some(
        (item) => isLineReceived(item) || isLineChargeback(item),
      );
      if (
        (purchaseOrder.delivery && !purchaseOrder.delivery?.delivered_at) ||
        !hasReceivedLineOrChargeback
      )
        return '-';

      return lastSubtotal && lastSubtotal.total && isFullyReceived
        ? numeral(lastSubtotal.total).format('$0,0.00')
        : numeral(sumPoItemsCost(rows)).format('$0,0.00');
    },
    label: '',
  };

  const initialState = {
    aggregation: {
      model: {
        item: 'total',
        quantity_received: 'qty_received',
        quantity: 'qty',
        ...(perms.canViewCost && { ext_cost: 'ext_cost' }),
      },
    },
    pinnedColumns: {
      left: ['item'],
    },
  };

  const baseAggregationFunctions = {
    total: totalAggregation,
    qty: quantityAggregation,
    qty_received: quantityReceivedAggregation,
  };

  const aggregationFunctions = perms.canViewCost
    ? {
        ...baseAggregationFunctions,
        ext_cost: extCostAggregation,
      }
    : baseAggregationFunctions;

  return (
    <>
      <DataGrid
        autosizeOnMount
        hideFilterButton
        hideGroupButton
        rows={transformedRows}
        pageSizeOptions={[15]}
        paginationMode="client"
        sortingMode="client"
        disableAggregation={false}
        autoHeight
        initialState={initialState}
        {...dataGridProps}
        columns={columns.filter(
          (col) =>
            !(
              (col.field === 'print_unit_label' && !canPrintUnit) ||
              (col.field === 'print_hang_tags' && !canPrintHang) ||
              (col.field === 'print_carton_label' && !canPrintCart) ||
              (col.field === 'prop65' && !flags.prop65?.enabled) ||
              ((col.field === 'unit_cost' || col.field === 'ext_cost') &&
                !perms.canViewCost)
            ),
        )}
        aggregationFunctions={aggregationFunctions}
      />
      {canPrintUnit && (
        <UnitLabelModal
          open={state.openPrintUnitLabel}
          onClose={closeModal}
          onConfirm={confirmPrintModal}
          lineInformation={state.printPOInformation}
          userId={userId}
        />
      )}
      {canPrintHang && (
        <HangtagModal
          open={state.openPrintHangtag}
          onClose={closeModal}
          onConfirm={confirmPrintModal}
          lineInformation={state.printPOInformation}
          userId={userId}
        />
      )}
      {canPrintCart && (
        <CartonLabelModal
          open={state.openPrintCartonLabel}
          onClose={closeModal}
          onConfirm={confirmPrintModal}
          lineInformation={state.printPOInformation}
          userId={userId}
          merchType={merchType}
        />
      )}
    </>
  );
}

export default PurchaseOrderDetailDataGrid;
